/**
 * Paywall, credit-gated unlock orchestration.
 *
 * - Single source of truth for credit costs (display only; backend enforces).
 * - useUnlock(action) gates the click via existing customer auth, calls
 *   postIntelligenceAction (which deducts credits server-side), and refreshes
 *   the navbar credit chip via getCredits.
 * - On INSUFFICIENT_CREDITS the helper opens the existing global
 *   `OutOfCredits` modal (mounted in the Navbar) via the
 *   `OutOFCreditsPopUpHandler` Redux action, no more redirect to
 *   /customer/wallet. `sendingCredits` is pre-seeded to the action cost
 *   so the modal's manual top-up input is helpful out of the box.
 */

import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { intelligenceCustomerGate } from "../../utils/intelligenceCustomerGate";
import { postIntelligenceAction } from "../../utils/intelligenceApi";
import { refreshCustomerCredits } from "../../../../common/utils/refreshCustomerCredits";
import { OutOFCreditsPopUpHandler } from "../../../../redux/features/customer/customerSlice";
import { loginPopupHandler } from "../../../../redux/features/auth/loginSlice";
import {
  trackLoginModalOpen,
  trackPaymentInitiate,
  trackPaymentSuccess,
} from "../../../../lib/funnels";

export const CREDIT_COSTS = {
  // Single-deed reveal (the "Unlock" pill on a registration row in
  // TransactionPreview, used everywhere recent registrations are listed:
  // SRO/Village deep-dive, party page, atlas page). Hits the
  // `intelligenceSearch` action, must match backend
  // INTELLIGENCE_SEARCH_CREDIT_COST.
  unlock_transaction: Number(
    import.meta.env.VITE_INTEL_SEARCH_CREDIT_COST ?? 9
  ),
  party_history: 1,
  // Genome reports (Due Diligence, Risk Audit, Area Intelligence,
  // Investment, Market Trend) and the property-search inline unlock,
  // all hit the same `intelligenceReport` action server-side, so this
  // single knob drives the Reveal pill on every genome surface.
  premium_report: Number(
    import.meta.env.VITE_INTEL_REPORT_CREDIT_COST ?? 50
  ),
  // Atlas insights and leaderboard are env-tunable so pricing can move
  // without a redeploy. Fallbacks match the rupee headline shown on
  // each CTA so display and charge stay in sync if env is unset.
  atlas_insights: Number(
    import.meta.env.VITE_INTEL_ATLAS_INSIGHTS_CREDIT_COST ?? 25
  ),
  leaderboard_full: Number(
    import.meta.env.VITE_INTEL_LEADERBOARD_CREDIT_COST ?? 9
  ),
  // Lease intelligence, single shared 25-credit tier for all three
  // unlocks (building profile, company profile, full ranked list).
  // Pricing tuned to match Atlas-insights so the unlock feels familiar
  // to existing intelligence users.
  lease_building_full: Number(
    import.meta.env.VITE_INTEL_LEASE_BUILDING_CREDIT_COST ?? 25
  ),
  lease_company_full: Number(
    import.meta.env.VITE_INTEL_LEASE_COMPANY_CREDIT_COST ?? 25
  ),
  lease_leaderboard_full: Number(
    import.meta.env.VITE_INTEL_LEASE_LEADERBOARD_CREDIT_COST ?? 25
  ),
  lease_location_full: Number(
    import.meta.env.VITE_INTEL_LEASE_LOCATION_CREDIT_COST ?? 25
  ),
  // Per-tenure micro-unlock on lease deep-dives. ₹9 per tenure
  // bucket (fresh / steady / anchor) for a single entity, lets
  // an analyst sample one cohort without buying the whole entity
  // reveal. Backend enforces in INTELLIGENCE_LEASE_TENURE_FEED_COST.
  lease_tenure_feed: Number(
    import.meta.env.VITE_INTEL_LEASE_TENURE_CREDIT_COST ?? 9
  ),
};

/**
 * Display-only INR price for each gated surface. Decoupled from the
 * credit cost so the user-facing rupee headline can be tuned without
 * touching the credit accounting.
 */
export const PAYWALL_PRICE_INR = {
  premium_report: Number(import.meta.env.VITE_INTEL_REPORT_PRICE_INR ?? 50),
  atlas_insights: Number(import.meta.env.VITE_INTEL_ATLAS_INSIGHTS_PRICE_INR ?? 25),
  leaderboard_full: Number(import.meta.env.VITE_INTEL_LEADERBOARD_PRICE_INR ?? 9),
  lease_building_full: Number(
    import.meta.env.VITE_INTEL_LEASE_BUILDING_PRICE_INR ?? 25
  ),
  lease_company_full: Number(
    import.meta.env.VITE_INTEL_LEASE_COMPANY_PRICE_INR ?? 25
  ),
  lease_leaderboard_full: Number(
    import.meta.env.VITE_INTEL_LEASE_LEADERBOARD_PRICE_INR ?? 25
  ),
  lease_location_full: Number(
    import.meta.env.VITE_INTEL_LEASE_LOCATION_PRICE_INR ?? 25
  ),
  lease_tenure_feed: Number(
    import.meta.env.VITE_INTEL_LEASE_TENURE_PRICE_INR ?? 9
  ),
};

/**
 * How many leaderboard rows render free before the paywall mask kicks
 * in. Defaults to 3; tweak `VITE_INTEL_LEADERBOARD_FREE_TOP` in the
 * env to bump it without touching the component.
 */
export const LEADERBOARD_FREE_TOP = Math.max(
  1,
  Number(import.meta.env.VITE_INTEL_LEADERBOARD_FREE_TOP ?? 3)
);

/**
 * DB (`intelligence` pricing singleton) → frontend key map. The Super-Admin
 * editable DB config is the source of truth for what checkout charges, so we
 * hydrate the display consts above from it — the env values are only the
 * pre-hydration fallback. Keys not present here (e.g. party_history) keep their
 * literal default.
 */
const DB_TO_FRONTEND_KEY = {
  search: "unlock_transaction",
  report: "premium_report",
  atlasInsights: "atlas_insights",
  leaderboardFull: "leaderboard_full",
  leaseBuildingFull: "lease_building_full",
  leaseCompanyFull: "lease_company_full",
  leaseLeaderboardFull: "lease_leaderboard_full",
  leaseLocationFull: "lease_location_full",
  leaseTenureFeed: "lease_tenure_feed",
};

let _pricingHydrated = false;

/**
 * Fetch the DB intelligence pricing once and mutate CREDIT_COSTS /
 * PAYWALL_PRICE_INR in place (they are imported by reference across the
 * intelligence UI, so every consumer picks up the DB value on its next render).
 * Safe to call repeatedly; only the first success sticks. Silently keeps the env
 * fallbacks on any failure.
 */
export async function hydrateIntelligencePricing() {
  if (_pricingHydrated) return;
  try {
    const base = String(import.meta.env.VITE_BASE_URI ?? "").replace(/\/$/, "");
    const res = await fetch(
      `${base}/api/property/service-pricing/singleton/intelligence`
    );
    if (!res.ok) return;
    const cfg = await res.json();
    if (!cfg || typeof cfg !== "object") return;
    for (const [dbKey, feKey] of Object.entries(DB_TO_FRONTEND_KEY)) {
      const v = Number(cfg[dbKey]);
      if (!Number.isFinite(v) || v < 0) continue;
      CREDIT_COSTS[feKey] = v;
      // INR display ≡ credit cost for these surfaces (see usePaywall), so keep
      // the rupee headline in sync with the charged credits.
      if (feKey in PAYWALL_PRICE_INR) PAYWALL_PRICE_INR[feKey] = v;
    }
    _pricingHydrated = true;
  } catch {
    /* keep env fallbacks */
  }
}

export function creditLabel(n) {
  const v = Number(n) || 0;
  return `${v} credit${v === 1 ? "" : "s"}`;
}

export function rupeeLabel(n) {
  const v = Number(n) || 0;
  return `₹${v.toLocaleString("en-IN")}`;
}

/**
 * One-shot paywall hook for "unlock-once-and-reveal" UI surfaces (atlas
 * insights, leaderboard rest-of-list). Wraps `useUnlock` with a local
 * `unlocked` flag and the standard wallet redirect on insufficient
 * credits. The page just reads `unlocked` to gate the reveal.
 *
 * Returns { unlocked, busy, error, charge }. Call `charge(payload?)`,
 * if `payload` is omitted, the bound payload from the hook arg is used.
 */
export function usePaywall(action, boundPayload, cost) {
  // Pass cost through so useUnlock can stamp the rupee `amount` on the
  // payment_initiate funnel event. Credit cost ≡ INR price for the
  // existing surfaces (9, 25, 50) so we forward it directly.
  const { unlock, busy, error, data } = useUnlock(action, { priceInr: cost });
  const [unlocked, setUnlocked] = useState(false);
  const dispatch = useDispatch();

  const charge = useCallback(
    async (override) => {
      if (unlocked || busy) return { ok: true, data };
      // Defensive: when wired straight to a button's onClick, React
      // hands us a SyntheticEvent. Stringifying that for the request
      // body crashes with a circular-reference error. Only treat
      // `override` as a real payload if it's a plain object literal.
      const isPlainPayload =
        override &&
        typeof override === "object" &&
        !override.nativeEvent &&
        !override.target;
      const payload = isPlainPayload ? override : boundPayload ?? {};
      const res = await unlock(payload);
      if (res.ok) {
        setUnlocked(true);
        return res;
      }
      if (res.error === "INSUFFICIENT_CREDITS") {
        promptTopUp(dispatch, cost);
      }
      // need_login / wrong_account, gate already showed the login popup
      return res;
    },
    [unlock, unlocked, busy, data, boundPayload, dispatch, cost]
  );

  // `data` is the raw response from the unlock action, used by
  // surfaces that need the full paid payload (e.g., leaderboard's
  // long-tail rows that the public endpoint refuses to ship).
  return { unlocked, busy, error, charge, data };
}

/**
 * Open the global out-of-credits modal that lives in the Navbar. Pre-
 * seeds `localStorage.sendingCredits` with the action cost so the
 * modal's manual top-up input lands at a useful number, the user can
 * still adjust before checkout.
 */
export function promptTopUp(dispatch, cost) {
  try {
    if (cost) {
      window.localStorage.setItem("sendingCredits", String(Number(cost) || 0));
    }
  } catch {
    /* localStorage unavailable, modal still opens, just no pre-fill */
  }
  dispatch(OutOFCreditsPopUpHandler(true));
}

/**
 * useUnlock(action)
 * Returns { unlock, busy, error, data }.
 * `unlock(payload)` triggers the gate and, on success, refreshes credits.
 */
export function useUnlock(action = "intelligenceSearch", opts = {}) {
  const { priceInr } = opts;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const unlock = useCallback(
    async (payload) => {
      setError(null);
      const gate = intelligenceCustomerGate(dispatch);
      if (gate !== "ok") {
        // Funnel #4 login gate, only fires on need_login (anonymous
        // user). `wrong_account` opens a different modal and isn't
        // part of the acquisition funnel. Stamp action+price so the
        // funnel report can roll up by surface.
        if (gate === "need_login") {
          trackLoginModalOpen(
            { trigger: "intelligence_unlock", step: 3 },
            { action, price: priceInr },
          );
        }
        return { ok: false, error: gate };
      }
      setBusy(true);
      // Funnel #4 Step 4, credits about to be debited server-side.
      // Fires BEFORE the await so a network hang still records the
      // payment intent. `amount` is the rupee price the user clicked,
      // not the credit count (they match today but the funnel reads
      // in INR).
      trackPaymentInitiate({
        product: "intelligence",
        amount: priceInr,
        action,
      });
      try {
        const res = await postIntelligenceAction({ action, payload });
        if (!res.ok) {
          // Server rejected an apparently-authed call (stale token,
          // server-side session expiry, etc.). Pre-flight gate above
          // only catches the no-token case; the 401 path needs to
          // reopen the global login modal explicitly.
          if (res.error === "UNAUTHORIZED") {
            dispatch(loginPopupHandler(true));
          }
          setError(res.error);
          return res;
        }
        setData(res.data);
        // Funnel #4 conversion-complete, credits actually debited and
        // the paid payload is in hand. Pairs with the payment_initiate
        // above; the delta between them is the payment-failure rate.
        trackPaymentSuccess({
          product: "intelligence",
          amount: priceInr,
          action,
        });
        // Pull fresh balance into Redux so the credit chip updates.
        refreshCustomerCredits(dispatch);
        // Invalidate any cached previews, fresh data may now be visible.
        queryClient.invalidateQueries({ queryKey: ["intel.report"] });
        queryClient.invalidateQueries({ queryKey: ["intel.party"] });
        return res;
      } catch (e) {
        const msg = e?.message || "Unlock failed";
        setError(msg);
        return { ok: false, error: msg };
      } finally {
        setBusy(false);
      }
    },
    [action, priceInr, dispatch, queryClient]
  );

  return { unlock, busy, error, data };
}
